import { GetInventorySerialHistory } from '@/apis/inventory';
import { CopyOUtlined } from '@/assets/icons/icons';
import ChevronRight from '@/assets/images/ChevronRight';
import { useToken } from '@/common/helper';
import { CustomButton, CustomPagination, CustomTitle } from '@/components';
import { useSpin } from '@/components/SpinContext';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { useUnAuthorize } from '@/hooks';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { Empty, Flex, message, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DeviceHistoryItem = {
  serial_number: string;
  in: string | null;
  activated: string | null;
  returned: string | null;
  time: string;
  title: string;
  units: string;
};

type DeviceHistoryPayload = {
  id: string;
  history: DeviceHistoryItem[];
  history_count: number;
  history_has_more: boolean;
  pagination: {
    page: number;
    page_size: number;
    total_pages: number;
    page_count: number;
    total_count: number;
  };
};

type DeviceHistoryEnvelope = {
  data: DeviceHistoryPayload;
};

type InventoryDeviceHistoryProps = {
  inventoryId: string;
  onOpenFullHistory: () => void;
};

type InventoryDeviceHistoryListProps = {
  inventoryId: string;
};

const COLLAPSED_ROW_COUNT = 3;
const PAGE_SIZE = 50;

const formatHistoryDate = (
  value: string | null,
  formatDateTime: (
    value?: string | null,
    options?: { includeTime?: boolean }
  ) => string
) => formatDateTime(value, { includeTime: false }) || '--';

const HistoryRows = ({ items }: { items: DeviceHistoryItem[] }) => {
  const { formatDateTime } = useUserDateTimeFormat();

  return (
    <div className='inventory-device-history-table'>
      <div className='inventory-device-history-row is-header'>
        <span>Serial #</span>
        <span>In</span>
        <span>Activated</span>
        <span>Returned</span>
      </div>
      {items.map((item, index) => (
        <div
          className='inventory-device-history-row'
          key={`${item.serial_number || 'unknown'}-${index}`}
        >
          <TruncateWithTooltip
            text={item.serial_number || 'Unknown'}
            max={18}
            render={(text) => (
              <span className='inventory-device-history-serial'>{text}</span>
            )}
          />
          <span>{formatHistoryDate(item.in, formatDateTime)}</span>
          <span>{formatHistoryDate(item.activated, formatDateTime)}</span>
          <span>{formatHistoryDate(item.returned, formatDateTime)}</span>
        </div>
      ))}
    </div>
  );
};

const useDeviceHistoryPage = (inventoryId: string, page: number) => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const [items, setItems] = useState<DeviceHistoryItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastRequestRef = useRef<string | null>(null);

  useEffect(() => {
    const requestKey = `${inventoryId}:${page}`;
    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    setLoading(true);
    GetInventorySerialHistory(inventoryId, token, page, PAGE_SIZE)
      .then((response) => {
        const envelope = response?.data
          ?.data as unknown as DeviceHistoryEnvelope;
        const history = envelope.data.history ?? [];

        setItems(history);
        setTotalPages(envelope.data.pagination.total_pages);
        setTotalCount(envelope.data.pagination.total_count);
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to load device history');
      })
      .finally(() => setLoading(false));
  }, [inventoryId, page, token, unauthorize]);

  return { items, loading, totalCount, totalPages };
};

const useCopyDeviceHistory = (inventoryId: string) => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const { showSpin, hideSpin } = useSpin();
  const { formatDateTime } = useUserDateTimeFormat();

  return useCallback(async () => {
    showSpin();

    try {
      const response = await GetInventorySerialHistory(
        inventoryId,
        token,
        0,
        0
      );
      const envelope = response?.data?.data as unknown as DeviceHistoryEnvelope;
      const history = envelope.data.history ?? [];

      if (!history.length) {
        message.warning('No history to copy');
        return;
      }

      const header = ['Serial #', 'In', 'Activated', 'Returned'].join('\t');
      const rows = history.map((item) =>
        [
          item.serial_number || 'Unknown',
          formatHistoryDate(item.in, formatDateTime),
          formatHistoryDate(item.activated, formatDateTime),
          formatHistoryDate(item.returned, formatDateTime),
        ].join('\t')
      );

      await navigator.clipboard.writeText([header, ...rows].join('\n'));
      message.success('Copied table');
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  }, [hideSpin, inventoryId, showSpin, token, unauthorize, formatDateTime]);
};

const HistoryCopyButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => (
  <CustomButton
    text='Copy'
    icon={<CopyOUtlined />}
    iconPosition='left'
    onClick={onClick}
    disabled={disabled}
    minWidth='52px'
    height='28px'
    padding='4px 8px'
    gap={8}
    bgColor='var(--light-gray)'
    color='var(--text-primary)'
    fontSize='12px'
    fontWeight={600}
    lineHeight='16px'
    borderRadius='8px'
    boxShadow='none'
  />
);

const InventoryDeviceHistory = ({
  inventoryId,
  onOpenFullHistory,
}: InventoryDeviceHistoryProps) => {
  const { items, loading } = useDeviceHistoryPage(inventoryId, 1);
  const visibleItems = useMemo(
    () => items.slice(0, COLLAPSED_ROW_COUNT),
    [items]
  );
  const handleCopy = useCopyDeviceHistory(inventoryId);

  return (
    <section className='inventory-device-history'>
      <Flex align='center' justify='space-between' gap={16}>
        <CustomTitle
          text='History'
          fontSize={14}
          fontWeight='600'
          lineHeight='16px'
          color='var(--text-primary)'
          marginBottom={0}
        />
        <Flex align='center' gap={8}>
          <HistoryCopyButton
            onClick={handleCopy}
            disabled={!visibleItems.length}
          />
          <button
            type='button'
            className='inventory-device-history-chevron'
            aria-label='Open full history'
            onClick={onOpenFullHistory}
          >
            <ChevronRight />
          </button>
        </Flex>
      </Flex>

      {loading ? (
        <Flex justify='center' className='inventory-device-history-loading'>
          <Spin size='small' />
        </Flex>
      ) : visibleItems.length ? (
        <HistoryRows items={visibleItems} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </section>
  );
};

export const InventoryDeviceHistoryList = ({
  inventoryId,
}: InventoryDeviceHistoryListProps) => {
  const [page, setPage] = useState(1);
  const { items, loading, totalCount, totalPages } = useDeviceHistoryPage(
    inventoryId,
    page
  );
  const handleCopy = useCopyDeviceHistory(inventoryId);

  return (
    <div className='inventory-history-list-content'>
      <Flex align='center' justify='space-between' gap={16}>
        <CustomTitle
          text={`History (${totalCount})`}
          fontSize={20}
          fontWeight='600'
          lineHeight='24px'
          color='var(--text-primary)'
          marginBottom={0}
        />
        <HistoryCopyButton
          onClick={handleCopy}
          disabled={!totalCount && !items.length}
        />
      </Flex>

      <div className='inventory-history-list-scroll'>
        {loading ? (
          <Flex justify='center' className='inventory-device-history-loading'>
            <Spin size='small' />
          </Flex>
        ) : items.length ? (
          <HistoryRows items={items} />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>

      {items.length > 0 && totalPages > 1 ? (
        <Flex justify='flex-end' className='inventory-history-pagination'>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Flex>
      ) : null}
    </div>
  );
};

export default InventoryDeviceHistory;
